import React, { useState } from 'react';
import { getCurrentGeoLocationPosition } from 'src/scripts/utils/location';
import FormInput from 'src/scripts/templates/_shared/ui-components/YogaUI/FormInput';
import { getGeocode } from './storeLocatorUtils';
import { ShippingCountryState } from '../../../../utils/shipping';
import { observer } from 'mobx-react-lite';
import { LatLng, LatLngError, StoreLocatorType } from './storeLocatorTypes';
import {
  customerLocationState,
  setCustomerLatLongLocation,
  setCustomerZipcode,
  setCustomerLocationErrorState,
  resetCustomerLatLongLocation,
  resetCustomerZipcode,
} from './customerLocationState';
import { bopisState } from '../Bopis/bopisState';
import { IVariant } from '../../types/PDP';
import { LoadingMoreSpinner } from '../../../product-listing-page/components/SmallUIComponents';
import { mapCountryCode } from '../FindInStore/findInStoresUtils';
import { reaction } from 'mobx';
import { useCartData } from '../../../../utils/cart';

interface CountryConfig {
  [key: string]: { regex: string; maxLength: number; postalOrZip: string };
}

const countryConfig: CountryConfig = {
  US: { regex: '^\\d{5}$', maxLength: 5, postalOrZip: 'zip' },
  CA: { regex: '^[A-Za-z]\\d[A-Za-z][ ]?\\d[A-Za-z]\\d$', maxLength: 7, postalOrZip: 'postal' },
  UK: {
    regex: '[a-zA-Z]{1,2}[0-9][0-9a-zA-Z]?\\s?[0-9][a-zA-Z]{2}$',
    maxLength: 8,
    postalOrZip: 'postal',
  },
};

let StoreLocatorForm = ({
  storeLocatorType,
  selectedVariant,
  page = 'pdp',
}: {
  storeLocatorType?: StoreLocatorType;
  selectedVariant: IVariant | undefined;
  page?: 'pdp' | 'cart';
}) => {
  const [resetKey, setResetKey] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const resetPostalInputValue = () => {
    setResetKey((prevKey: number) => prevKey + 1);
  };

  React.useEffect(
    () =>
      reaction(
        () => customerLocationState.zip,
        (zip) => {
          resetPostalInputValue();
        }
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const cartData = useCartData({ watchForChanges: false });

  const countryConfigEntry = countryConfig[mapCountryCode(ShippingCountryState.countryCode)];
  if (!countryConfigEntry) {
    console.error(`Invalid country code: ${ShippingCountryState.countryCode}`);
    return null;
  }
  const { regex, maxLength, postalOrZip } = countryConfigEntry;
  const label = `Search by ${postalOrZip} code`;
  const inputErrorMessage = `Please enter a valid ${postalOrZip} code.`;

  const onFormSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const form = event.target as HTMLFormElement;
    const formData = new FormData(form);
    const postal = (
      formData.get(storeLocatorType === 'fis' ? 'postalCodeFindInStore' : 'postalCodeBopis')?.toString() || ''
    )
      .replace(/\s+/g, ' ')
      .trim()
      .toUpperCase();

    try {
      const params = {
        postal_code: postal,
        country_code: mapCountryCode(ShippingCountryState.countryCode).toLowerCase(),
      };
      setCustomerLocationErrorState(false);
      if (params.postal_code) {
        const geocodeData: LatLng | LatLngError = await getGeocode(params);
        if ('latitude' in geocodeData && 'longitude' in geocodeData) {
          const { latitude, longitude } = geocodeData;
          if (latitude && longitude) {
            setCustomerZipcode(postal);
            setCustomerLatLongLocation(latitude.toString(), longitude.toString());
          }
        } else {
          // if error is 400, geocode api service is down but can still fetch Bopis Data with Zip
          if (geocodeData.error.status === 400) {
            setCustomerZipcode(postal); // set the zip so we can still make bopis fetch
            if (selectedVariant?.sku && storeLocatorType === 'bopis') bopisState.fetchStoresDataPdp(selectedVariant);
            if (page === 'cart' && storeLocatorType === 'bopis') bopisState.fetchStoresDataCart(cartData);
            if (storeLocatorType === 'fis') setCustomerLocationErrorState(true);
          } else {
            // else, the error is 404 which means it's an invalid zip and should show user invalid zip error
            setCustomerLocationErrorState(true);
          }
          throw new Error('Latitude or Longitude is not defined.');
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getCurrentLocation = async () => {
    setIsLoading(true);
    setCustomerLocationErrorState(false);
    try {
      const data = await getCurrentGeoLocationPosition();
      const { latitude, longitude } = data;
      setCustomerLatLongLocation(latitude.toString(), longitude.toString());
      resetPostalInputValue();
      resetCustomerZipcode();
      setIsLoading(false);
      return { success: true };
    } catch (err) {
      resetCustomerLatLongLocation();
      setIsLoading(false);
      return { success: false };
    }
  };

  const handleCurrentLocation = async (event: React.FormEvent) => {
    event.preventDefault();
    getCurrentLocation();
  };

  return (
    <>
      <div className="store-locator-form-container d-flex">
        <form className="store-locator-form d-flex" onSubmit={onFormSubmit}>
          <FormInput
            key={resetKey} // Rerenders the input on coords change due to FormInput being uncontrolled React input. [REVISIT] Should FormInput be controlled for more flexibility???
            options={{
              type: 'text',
              id: storeLocatorType === 'fis' ? 'postalCodeFindInStore' : 'postalCodeBopis',
              name: storeLocatorType === 'fis' ? 'postalCodeFindInStore' : 'postalCodeBopis',
              errorText: inputErrorMessage,
              label: storeLocatorType === 'fis' ? label : 'Search by zip code',
              pattern: regex,
              defaultValue: customerLocationState.zip ?? undefined,
              maxLength: maxLength,
              required: true,
              showErrorOnBlur: false,
            }}
          />
          <button className="button-primary arrow-button" type="submit">
            <span className="alo-chevron-right-icon"></span>
          </button>
        </form>
        {isLoading ? (
          <div>
            <LoadingMoreSpinner />
          </div>
        ) : (
          <div className="current-location-link d-flex">
            or
            <span className="current-location-link__button" onClick={handleCurrentLocation}>
              <span>Use my current location</span>
            </span>
          </div>
        )}
      </div>
      {customerLocationState.apiError && (
        <p className="alert-text">That {postalOrZip} code does not exist. Please try a different one.</p>
      )}
    </>
  );
};

StoreLocatorForm = observer(StoreLocatorForm);
export { StoreLocatorForm };
